import React, { useEffect, useState } from "react";
import { Grid, CircularProgress } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { useDispatch, useSelector } from "react-redux";
import GroupIcon from "@mui/icons-material/Group";
import {
  PendingActions,
  TodayOutlined,
  Square,
  Circle,
  AccessTime,
  CakeSharp,
  Portrait,
  EventBusy,
  Schedule,
  GroupsOutlined,
} from "@mui/icons-material";
import Avatar from "@mui/material/Avatar";
import { format, isToday, isTomorrow, parseISO } from "date-fns";
import { getUserInfoFromToken } from "../utils/authUtils";
import ProtectedImage from "./ProtectedImage";
import { Link, useNavigate } from "react-router-dom";
import AuthViewController from "../redux/actions/AuthViewController";
import "../styles/layout.css";
import "../styles/dashboard.css";

const ATTENDANCE_COLORS = ["#03c95a", "#1a2b4a", "#E65A00", "#e70d0d"];

const STATUS_ITEMS = [
  { key: "Full Time", label: "Fulltime", countKey: "full-time-count", colorClass: "text-primary" },
  { key: "Contract", label: "Contract", countKey: "contract-count", colorClass: "text-secondary" },
  { key: "Probation", label: "Probation", countKey: "probation-count", colorClass: "text-danger" },
  { key: "Work From Home", label: "WFH", countKey: "wfh-count", colorClass: "text-pink" },
];

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getEmployerDashboardData } = AuthViewController();
  const { employerdashboardData, loading, error, userDetails } = useSelector(
    (state) => state.auth
  );
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    const { userType: type } = getUserInfoFromToken();
    setUserType(type);
  }, []);

  useEffect(() => {
    if (userType === "Employer") {
      dispatch(getEmployerDashboardData());
    }
  }, [dispatch, userType]);

  const data = employerdashboardData;

  const attendanceChartData = [
    { name: "Present", value: data?.present_employees_today || 0 },
    { name: "Late", value: data?.late_employees_today || 0 },
    { name: "On Leave", value: data?.on_leave_today || 0 },
    { name: "Absent", value: data?.absent_employees_today || 0 },
  ];
  const attendanceTotal =
    attendanceChartData.reduce((sum, item) => sum + item.value, 0) || 1;
  const hasAttendanceData = attendanceChartData.some((item) => item.value > 0);

  const last4ClockIns = data?.last_4_clock_ins || [];
  const parseTime = (timeStr) => {
    const [hours, minutes, seconds] = timeStr.split(":").map(Number);
    return new Date(1970, 0, 1, hours, minutes, seconds);
  };

  let lateEmployee = null;
  let earlyEmployees = [];
  if (last4ClockIns.length > 0) {
    lateEmployee = [...last4ClockIns].sort(
      (a, b) => parseTime(b.clock_in_time) - parseTime(a.clock_in_time)
    )[0];
    earlyEmployees = last4ClockIns.filter((emp) => emp !== lateEmployee);
  }

  const todayBirthdays =
    data?.upcoming_birthdays?.filter((item) =>
      isToday(parseISO(item.date_of_birth))
    ) || [];
  const tomorrowBirthdays =
    data?.upcoming_birthdays?.filter((item) =>
      isTomorrow(parseISO(item.date_of_birth))
    ) || [];
  const futureBirthdays =
    data?.upcoming_birthdays?.filter(
      (item) =>
        !isToday(parseISO(item.date_of_birth)) &&
        !isTomorrow(parseISO(item.date_of_birth))
    ) || [];

  const combinedBirthdays = [
    ...todayBirthdays.map((item) => ({ ...item, group: "today" })),
    ...tomorrowBirthdays.map((item) => ({ ...item, group: "tomorrow" })),
    ...futureBirthdays.map((item) => ({ ...item, group: "future" })),
  ].slice(0, 4);

  const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length > 1) {
      return (
        parts[0].charAt(0).toUpperCase() + parts[1].charAt(0).toUpperCase()
      );
    }
    return parts[0].charAt(0).toUpperCase();
  };

  const pct = (num, denom) =>
    denom ? `${((num / denom) * 100).toFixed(0)}%` : "0%";

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const renderAvatar = (emp, size = 48) => (
    <Avatar
      sx={{
        width: size,
        height: size,
        border: "2px solid #e5e7eb",
        bgcolor: "var(--brand-navy)",
      }}
    >
      {emp.profile_image ? (
        <ProtectedImage
          path={emp.profile_image}
          alt={emp.name || "Profile"}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "50%",
          }}
        />
      ) : (
        <span style={{ color: "white", fontSize: "16px" }}>
          {getInitials(emp.name)}
        </span>
      )}
    </Avatar>
  );

  const renderClockItem = (emp, isLate = false) => (
    <div className="dashboard-clock-item" key={`${emp.name}-${emp.clock_in_time}`}>
      <div className="d-flex align-items-center">
        {renderAvatar(emp, 44)}
        <div className="ms-2">
          <h6 className="fs-14 fw-medium text-truncate mb-0">{emp.name}</h6>
          <p className="fs-13 mb-0">{emp.designation || "—"}</p>
        </div>
      </div>
      <div className="d-flex align-items-center">
        <AccessTime sx={{ fontSize: 18, color: "var(--brand-muted)", mr: 1 }} />
        <span
          className="fs-10 fw-medium d-inline-flex align-items-center badge badge-success"
          style={isLate ? { background: "var(--brand-orange)" } : undefined}
        >
          <Circle className="fs-5 me-1 white-icon" />
          {emp.clock_in_time?.slice(0, 5)}
        </span>
      </div>
    </div>
  );

  if (!userType || userType !== "Employer") {
    return (
      <div className="dashboard-loading">
        <CircularProgress size={32} sx={{ color: "var(--brand-orange)" }} />
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="dashboard-loading">
        <CircularProgress size={32} sx={{ color: "var(--brand-orange)" }} />
      </div>
    );
  }

  return (
    <div className="dashboard-page">

      {error && !data && (
        <div className="dashboard-error-banner">
          Unable to load dashboard data. Please refresh the page or log in again
          if the problem persists.
        </div>
      )}

      <div className="dashboard-mobile-welcome">
        <h1 className="dashboard-mobile-greeting">
          {getGreeting()}
          {userDetails?.first_name ? `, ${userDetails.first_name}` : ""}
        </h1>
        <p className="dashboard-mobile-date">
          {format(new Date(), "EEEE, MMMM d, yyyy")}
        </p>
      </div>

      <div className="dashboard-stats list-page-stats">
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={6} md={3}>
            <div
              className="stat-card stat-card-compact"
              onClick={() => navigate("/time-attendance")}
              role="button"
              tabIndex={0}
            >
              <div className="avatar rounded-circle bg-primary">
                <TodayOutlined className="white-icon" />
              </div>
              <div className="stat-card-compact-body">
                <h6>Attendance</h6>
                <h3 className="value">
                  {data?.present_employees_today || 0} /{" "}
                  {data?.total_employees || 0}
                  <span className="fs-12 fw-medium text-success ms-2">
                    {pct(
                      data?.present_employees_today,
                      data?.total_employees
                    )}
                  </span>
                </h3>
              </div>
            </div>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <div
              className="stat-card stat-card-compact"
              onClick={() => navigate("/users")}
              role="button"
              tabIndex={0}
            >
              <div className="avatar rounded-circle bg-info">
                <GroupIcon className="white-icon" />
              </div>
              <div className="stat-card-compact-body">
                <h6>
                  <span className="stat-label-full">Active Employees</span>
                  <span className="stat-label-short">Employees</span>
                </h6>
                <h3 className="value">
                  {data?.active_employees || 0} / {data?.total_employees || 0}
                  <span className="fs-12 fw-medium text-success ms-2">
                    {pct(data?.active_employees, data?.total_employees)}
                  </span>
                </h3>
              </div>
            </div>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <div
              className="stat-card stat-card-compact"
              onClick={() => navigate("/departments")}
              role="button"
              tabIndex={0}
            >
              <div className="avatar rounded-circle bg-primary">
                <Portrait className="white-icon" />
              </div>
              <div className="stat-card-compact-body">
                <h6>Departments</h6>
                <h3 className="value">{data?.total_departments || 0}</h3>
              </div>
            </div>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <div
              className="stat-card stat-card-compact"
              onClick={() => navigate("/leaves")}
              role="button"
              tabIndex={0}
            >
              <div className="avatar rounded-circle bg-danger">
                <PendingActions className="white-icon" />
              </div>
              <div className="stat-card-compact-body">
                <h6>
                  <span className="stat-label-full">Pending Requests</span>
                  <span className="stat-label-short">Pending</span>
                </h6>
                <h3 className="value">
                  {data?.pending_leave_requests || 0} /{" "}
                  {data?.total_leave_requests || 0}
                  <span className="fs-12 fw-medium text-success ms-2">
                    {pct(
                      data?.pending_leave_requests,
                      data?.total_leave_requests
                    )}
                  </span>
                </h3>
              </div>
            </div>
          </Grid>
        </Grid>
      </div>

      <div className="dashboard-section-grid">
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h5 className="dashboard-card-title">Employee Status</h5>
                <p className="dashboard-card-subtitle">
                  Breakdown by employment type
                </p>
              </div>
              <div className="dashboard-total-row">
                <p>Total Employees</p>
                <h3>{data?.total_employees || 0}</h3>
              </div>
              <div className="dashboard-status-grid">
                {STATUS_ITEMS.map((item) => (
                  <div className="dashboard-status-item" key={item.key}>
                    <p className="status-label">
                      <Square className={`fs-12 ${item.colorClass}`} />{" "}
                      {item.label}{" "}
                      <span className="text-gray-9">
                        (
                        {data?.employee_status_summary?.[item.key]?.percentage ||
                          0}
                        %)
                      </span>
                    </p>
                    <p className="status-value">
                      {data?.employee_status_summary?.[item.key]?.[
                        item.countKey
                      ] || 0}
                    </p>
                  </div>
                ))}
              </div>
              <Link
                to="/users"
                className="dashboard-action-btn dashboard-action-btn--primary"
              >
                View All Employees
              </Link>
            </div>

            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h5 className="dashboard-card-title">Attendance Overview</h5>
                <p className="dashboard-card-subtitle">Today&apos;s snapshot</p>
              </div>
              <div className="dashboard-chart-wrap">
                {hasAttendanceData ? (
                  <PieChart width={200} height={200}>
                    <Pie
                      data={attendanceChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      dataKey="value"
                      paddingAngle={5}
                    >
                      {attendanceChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={ATTENDANCE_COLORS[index]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                ) : (
                  <div className="dashboard-chart-empty-ring">
                    <EventBusy className="dashboard-empty-icon" />
                    <span>No attendance today</span>
                  </div>
                )}
              </div>
              <h6 className="dashboard-card-title mb-2">Status</h6>
              {attendanceChartData.map((item, index) => (
                <div className="dashboard-legend-row" key={item.name}>
                  <p>
                    <Circle
                      className="me-1"
                      style={{ fontSize: 10, color: ATTENDANCE_COLORS[index] }}
                    />
                    {item.name}
                  </p>
                  <p className="legend-pct">
                    {((item.value / attendanceTotal) * 100).toFixed(0)}%
                  </p>
                </div>
              ))}
              <Link
                to="/time-attendance"
                className="dashboard-action-btn dashboard-action-btn--primary"
              >
                View Attendance
              </Link>
            </div>
          </div>

          <div className="dashboard-bottom-grid">
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h5 className="dashboard-card-title">Clock-In / Out</h5>
                <p className="dashboard-card-subtitle">
                  Recent check-ins for today
                </p>
              </div>

              {last4ClockIns.length > 0 ? (
                <>
                  {earlyEmployees.map((emp) => renderClockItem(emp))}
                  {lateEmployee && (
                    <>
                      <h6 className="dashboard-birthday-group">Late</h6>
                      {renderClockItem(lateEmployee, true)}
                    </>
                  )}
                </>
              ) : (
                <div className="dashboard-empty">
                  <Schedule className="dashboard-empty-icon" />
                  <p>No clock-ins yet today</p>
                  <p className="dashboard-empty-hint">
                    Attendance records will appear here once employees check in
                  </p>
                </div>
              )}

              <Link to="/time-attendance" className="dashboard-action-btn">
                View All Attendance
              </Link>
            </div>

            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h5 className="dashboard-card-title">Upcoming Birthdays</h5>
                <p className="dashboard-card-subtitle">
                  Celebrate your team members
                </p>
              </div>

              {combinedBirthdays.length > 0 ? (
                <>
                  {combinedBirthdays.some((b) => b.group === "today") && (
                    <>
                      <p className="dashboard-birthday-group">Today</p>
                      {combinedBirthdays
                        .filter((item) => item.group === "today")
                        .map((item, index) => (
                          <div
                            className="dashboard-birthday-item"
                            key={`today-${index}`}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                {renderAvatar(item, 44)}
                                <div className="ms-2">
                                  <h6 className="fs-14 fw-medium text-truncate mb-0">
                                    {item.name}
                                  </h6>
                                  <p className="fs-13 mb-0">
                                    {item.designation || "—"}
                                  </p>
                                </div>
                              </div>
                              <CakeSharp
                                sx={{ color: "var(--brand-orange)", fontSize: 22 }}
                              />
                            </div>
                          </div>
                        ))}
                    </>
                  )}

                  {combinedBirthdays.some((b) => b.group === "tomorrow") && (
                    <>
                      <p className="dashboard-birthday-group">Tomorrow</p>
                      {combinedBirthdays
                        .filter((item) => item.group === "tomorrow")
                        .map((item, index) => (
                          <div
                            className="dashboard-birthday-item"
                            key={`tomorrow-${index}`}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                {renderAvatar(item, 44)}
                                <div className="ms-2">
                                  <h6 className="fs-14 fw-medium text-truncate mb-0">
                                    {item.name}
                                  </h6>
                                  <p className="fs-13 mb-0">
                                    {item.designation || "—"}
                                  </p>
                                </div>
                              </div>
                              <CakeSharp
                                sx={{ color: "var(--brand-orange)", fontSize: 22 }}
                              />
                            </div>
                          </div>
                        ))}
                    </>
                  )}

                  {combinedBirthdays
                    .filter((item) => item.group === "future")
                    .map((item, index) => (
                      <div key={`future-${index}`}>
                        <p className="dashboard-birthday-group">
                          {format(parseISO(item.date_of_birth), "dd MMM yyyy")}
                        </p>
                        <div className="dashboard-birthday-item">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              {renderAvatar(item, 44)}
                              <div className="ms-2">
                                <h6 className="fs-14 fw-medium text-truncate mb-0">
                                  {item.name}
                                </h6>
                                <p className="fs-13 mb-0">
                                  {item.designation || "—"}
                                </p>
                              </div>
                            </div>
                            <CakeSharp
                              sx={{ color: "var(--brand-orange)", fontSize: 22 }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                </>
              ) : (
                <div className="dashboard-empty">
                  <GroupsOutlined className="dashboard-empty-icon" />
                  <p>No upcoming birthdays</p>
                  <p className="dashboard-empty-hint">
                    Add date of birth in employee profiles to see birthdays here
                  </p>
                </div>
              )}
            </div>
          </div>
    </div>
  );
};

export default Dashboard;
